import { invisiblyApi } from 'src/redux/api/invisibly-api';
import { APITagType } from 'src/redux/api/invisibly-api.types';
import type {
  ReportingApiAlerts,
  ReportingApiClientDetail,
  ReportingApiClientParams,
  ReportingApiClientRow,
  ReportingApiEndpointRow,
  ReportingApiErrorRow,
  ReportingApiErrorsParams,
  ReportingApiKeysParams,
  ReportingApiKeyRow,
  ReportingApiQueryShapeRow,
  ReportingApiQuotaStateRow,
  ReportingApiSummary,
  ReportingApiWindowParams,
} from '../types/reporting-api-usage.types';

const reportingApiUsageEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    getReportingApiSummary: builder.query<ReportingApiSummary, ReportingApiWindowParams | void>({
      query: ({ days } = {}) => ({
        method: 'GET',
        url: 'b2b/operations/reporting-api/summary',
        params: days ? { days } : undefined,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: ReportingApiSummary }) => response.item,
      providesTags: [{ id: 'SUMMARY', type: APITagType.ReportingApiUsage }],
    }),
    getReportingApiClients: builder.query<{ clients: ReportingApiClientRow[] }, ReportingApiWindowParams | void>({
      query: ({ days } = {}) => ({
        method: 'GET',
        url: 'b2b/operations/reporting-api/clients',
        params: days ? { days } : undefined,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: { clients: ReportingApiClientRow[] } }) => response.item,
      providesTags: [{ id: 'CLIENTS', type: APITagType.ReportingApiUsage }],
    }),
    getReportingApiClientDetail: builder.query<ReportingApiClientDetail, ReportingApiClientParams>({
      query: ({ clientId, days }) => ({
        method: 'GET',
        url: `b2b/operations/reporting-api/clients/${clientId}`,
        params: days ? { days } : undefined,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: ReportingApiClientDetail }) => response.item,
      providesTags: (_result, _error, { clientId }) => [
        { id: clientId, type: APITagType.ReportingApiUsage },
      ],
    }),
    getReportingApiUsageKeys: builder.query<{ keys: ReportingApiKeyRow[] }, ReportingApiKeysParams | void>({
      query: ({ days, client_id: clientId } = {}) => ({
        method: 'GET',
        url: 'b2b/operations/reporting-api/keys',
        params: {
          ...(days ? { days } : {}),
          ...(clientId ? { client_id: clientId } : {}),
        },
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: { keys: ReportingApiKeyRow[] } }) => response.item,
      providesTags: [{ id: 'KEYS', type: APITagType.ReportingApiUsage }],
    }),
    getReportingApiEndpoints: builder.query<
    { by_endpoint: ReportingApiEndpointRow[]; by_query_shape: ReportingApiQueryShapeRow[] },
    ReportingApiWindowParams | void
    >({
      query: ({ days } = {}) => ({
        method: 'GET',
        url: 'b2b/operations/reporting-api/endpoints',
        params: days ? { days } : undefined,
        handleErrorGlobally: false,
      }),
      transformResponse: (response: {
        item: { by_endpoint: ReportingApiEndpointRow[]; by_query_shape: ReportingApiQueryShapeRow[] };
      }) => response.item,
      providesTags: [{ id: 'ENDPOINTS', type: APITagType.ReportingApiUsage }],
    }),
    getReportingApiErrors: builder.query<{ errors: ReportingApiErrorRow[] }, ReportingApiErrorsParams | void>({
      query: ({ limit, error_code: errorCode } = {}) => ({
        method: 'GET',
        url: 'b2b/operations/reporting-api/errors',
        params: {
          ...(limit ? { limit } : {}),
          ...(errorCode ? { error_code: errorCode } : {}),
        },
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: { errors: ReportingApiErrorRow[] } }) => response.item,
      providesTags: [{ id: 'ERRORS', type: APITagType.ReportingApiUsage }],
    }),
    getReportingApiQuotaState: builder.query<{ clients: ReportingApiQuotaStateRow[] }, void>({
      query: () => ({
        method: 'GET',
        url: 'b2b/operations/reporting-api/quota-state',
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: { clients: ReportingApiQuotaStateRow[] } }) => response.item,
      providesTags: [{ id: 'QUOTA', type: APITagType.ReportingApiUsage }],
    }),
    getReportingApiAlerts: builder.query<ReportingApiAlerts, void>({
      query: () => ({
        method: 'GET',
        url: 'b2b/operations/reporting-api/alerts',
        handleErrorGlobally: false,
      }),
      transformResponse: (response: { item: ReportingApiAlerts }) => response.item,
      providesTags: [{ id: 'ALERTS', type: APITagType.ReportingApiUsage }],
    }),
  }),
});

export const {
  useGetReportingApiSummaryQuery,
  useGetReportingApiClientsQuery,
  useGetReportingApiClientDetailQuery,
  useGetReportingApiUsageKeysQuery,
  useGetReportingApiEndpointsQuery,
  useGetReportingApiErrorsQuery,
  useGetReportingApiQuotaStateQuery,
  useGetReportingApiAlertsQuery,
} = reportingApiUsageEndpoints;
